import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Typography,
  Paper,
  Chip,
  Tooltip,
  IconButton,
  Button,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { loadBillingFilters, saveBillingFilters } from 'Includes/billingFilterStorage';
import OrdersSourceToggle, { loadOrdersSource } from 'Components/OrdersSourceToggle';
import { TEAM_STATUS_CHOICES } from '../../Includes/teamStatus';
import CoordinatorMapModal from 'Components/CoordinatorMapModal';
import ReportsMobileBottomNav from 'Components/ReportsMobileBottomNav';

function CoordinatorWiseReportList(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const storedFilters = loadBillingFilters();

  const [academicYearList, setAcademicYearList] = React.useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = React.useState(storedFilters.academicYearId || '');
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all');
  const [clientBillingStatusFilter, setClientBillingStatusFilter] = React.useState('');
  const [serviceList, setServiceList] = React.useState([]);
  const [selectedService, setSelectedService] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [mapModalOpen, setMapModalOpen] = React.useState(false);
  const [selectedCoordinator, setSelectedCoordinator] = React.useState(null);
  const [orderSource, setOrderSource] = React.useState(loadOrdersSource());

  const formatAmount = (v) => {
    const n = parseFloat(v || 0);
    return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  React.useEffect(() => {
    getAcademicYearList();
    getServiceList();
  }, []);

  const getAcademicYearList = () => {
    getRequest(GET_URL.academicyear.api, {}, props).then((res) => {
      if (res?.status === 200) {
        const list = res.data.data || [];
        setAcademicYearList(list);

        if (selectedAcademicYear) {
          fetchCoordinatorSummary(selectedAcademicYear);
        }
      }
    });
  };

  const getServiceList = () => {
    getRequest(GET_URL.services?.api, {}, props).then((res) => {
      if (res?.status === 200) {
        setServiceList(res.data.data || []);
      }
    });
  };

  const fetchCoordinatorSummary = (yearId) => {
    if (!yearId) return;

    setLoading(true);

    const params = {
      year: yearId,
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,
      ...(statusByTeamFilter && { status_by_team: statusByTeamFilter }),
      ...(clientBillingStatusFilter && { client_billing_status: clientBillingStatusFilter }),
      ...(selectedService?.id && { service: selectedService.id }),
      order_source: orderSource,
    };

    getRequest(GET_URL.billing_config.api, { ...params, coordinator_wise_summary: 1 }, props)
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) {
          const data = res.data?.data || [];

          const transformed = data.map((item, index) => ({
            id: index + 1,
            coordinator_id: item.coordinator_id,
            coordinator_name: item.coordinator_name || 'Unassigned',
            order_total_amount: item.totals?.order || 0,
            issued_invoice_total_amount: item.totals?.issued || 0,
            issue_invoice_total_amount: item.totals?.invoiced || 0,
            paid_total_amount: item.totals?.paid || 0,
            discount_amount: item.totals?.discount || 0,
            balance_amount: item.totals?.balance || 0,
            balance_to_be_paid_by_today: item.totals?.balanceToPayByToday || 0,
            no_of_companies: item.no_of_companies || 0,
          }));

          setRows(transformed);
          setRowCount(res.data?.count || transformed.length);
        }
      })
      .catch(() => setLoading(false));
  };

  React.useEffect(() => {
    if (selectedAcademicYear) {
      fetchCoordinatorSummary(selectedAcademicYear);
    }
  }, [statusByTeamFilter, clientBillingStatusFilter, selectedService, paginationModel, selectedAcademicYear, orderSource]);

  const totals = React.useMemo(() => {
    let order = 0;
    let paid = 0;
    let balance = 0;
    let due = 0;
    let companies = 0;
    rows.forEach((r) => {
      order += parseFloat(r.order_total_amount || 0);
      paid += parseFloat(r.paid_total_amount || 0);
      balance += parseFloat(r.balance_amount || 0);
      due += parseFloat(r.balance_to_be_paid_by_today || 0);
      companies += parseInt(r.no_of_companies || 0, 10);
    });
    return { order, paid, balance, due, companies };
  }, [rows]);

  const columns = [
    {
      field: 'coordinator_name',
      headerName: 'Coordinator',
      minWidth: 180,
      flex: 1,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color="#0f172a">
          {params.value}
        </Typography>
      )
    },
    {
      field: 'order_total_amount',
      headerName: 'Order Total',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="#1e293b">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'issued_invoice_total_amount',
      headerName: 'Issued Invoice',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" color="#475569">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'paid_total_amount',
      headerName: 'Paid',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} color="#16a34a">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'balance_amount',
      headerName: 'Balance',
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="#ea580c">
          {formatAmount(params.value)}
        </Typography>
      )
    },
    {
      field: 'balance_to_be_paid_by_today',
      headerName: 'Due Today',
      width: 145,
      renderCell: (params) => {
        const val = parseFloat(params.value || 0);
        return (
          <Typography
            variant="body2"
            fontWeight={val > 0 ? 800 : 500}
            color={val > 0 ? '#dc2626' : '#64748b'}
          >
            {formatAmount(val)}
          </Typography>
        );
      }
    },
    {
      field: 'no_of_companies',
      headerName: 'Clients',
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value || 0}
          sx={{ fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f1f5f9', color: '#334155' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Map',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="View Coordinator Map">
          <IconButton
            size="small"
            sx={{
              backgroundColor: '#f1f5f9',
              '&:hover': { backgroundColor: '#e2e8f0', color: '#0284c7' }
            }}
            onClick={() => {
              setSelectedCoordinator(params.row);
              setMapModalOpen(true);
            }}
          >
            <MapOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box p={{ xs: 1.5, sm: 2.5 }} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>

      {/* HEADER */}
      <Box mb={2}>
        <Grid container alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
              Coordinator Wise Report
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Billing totals, collections, and pending balances by coordinator
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, alignItems: 'center' }}>
            <OrdersSourceToggle
              value={orderSource}
              onChange={(next) => {
                setOrderSource(next);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* KPI SUMMARY CARDS */}
      {selectedAcademicYear && (
        <Grid container spacing={1.5} mb={2}>
          {/* Coordinators Count */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <PersonOutlineIcon sx={{ fontSize: 16, color: '#64748b' }} />
                <Typography variant="caption" fontWeight={700} color="#64748b" textTransform="uppercase" fontSize="0.65rem">
                  Coordinators
                </Typography>
              </Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800} color="#0f172a">
                {rows.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totals.companies} Clients
              </Typography>
            </Paper>
          </Grid>

          {/* Order Total */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                <Typography variant="caption" fontWeight={700} color="#3b82f6" textTransform="uppercase" fontSize="0.65rem">
                  Orders
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#0f172a" noWrap>
                {formatAmount(totals.order)}
              </Typography>
            </Paper>
          </Grid>

          {/* Collected */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #bbf7d0', backgroundColor: '#f0fdf4' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                <Typography variant="caption" fontWeight={700} color="#16a34a" textTransform="uppercase" fontSize="0.65rem">
                  Collected
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#15803d" noWrap>
                {formatAmount(totals.paid)}
              </Typography>
            </Paper>
          </Grid>

          {/* Balance */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: '1px solid #fed7aa', backgroundColor: '#fff7ed' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16, color: '#ea580c' }} />
                <Typography variant="caption" fontWeight={700} color="#ea580c" textTransform="uppercase" fontSize="0.65rem">
                  Pending
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color="#c2410c" noWrap>
                {formatAmount(totals.balance)}
              </Typography>
            </Paper>
          </Grid>

          {/* Due Today */}
          <Grid item xs={12} sm={4} md={2.4}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: totals.due > 0 ? '1.5px solid #fecaca' : '1px solid #e2e8f0', backgroundColor: totals.due > 0 ? '#fef2f2' : '#ffffff' }}>
              <Box display="flex" alignItems="center" gap={0.8} mb={0.3}>
                <ErrorOutlineOutlinedIcon sx={{ fontSize: 16, color: totals.due > 0 ? '#dc2626' : '#64748b' }} />
                <Typography variant="caption" fontWeight={700} color={totals.due > 0 ? '#dc2626' : '#64748b'} textTransform="uppercase" fontSize="0.65rem">
                  Due Today
                </Typography>
              </Box>
              <Typography variant="subtitle1" fontWeight={800} color={totals.due > 0 ? '#b91c1c' : '#0f172a'} noWrap>
                {formatAmount(totals.due)}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* STICKY FILTERS */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: { xs: 66, sm: 66 },
          zIndex: 90,
          p: 1.5,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid #e2e8f0',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Grid container spacing={1.5}>
          <Grid item md={3} xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={selectedAcademicYear}
                onChange={(e) => {
                  setSelectedAcademicYear(e.target.value);
                  saveBillingFilters({ academicYearId: e.target.value });
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                label="Academic Year"
                sx={{ borderRadius: 2 }}
              >
                {academicYearList.map((y) => (
                  <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={6} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status (Team)</InputLabel>
              <Select
                value={statusByTeamFilter}
                label="Status (Team)"
                onChange={(e) => {
                  setStatusByTeamFilter(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                sx={{ borderRadius: 2 }}
              >
                {TEAM_STATUS_CHOICES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={6} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Billing Status</InputLabel>
              <Select
                value={clientBillingStatusFilter}
                label="Billing Status"
                onChange={(e) => {
                  setClientBillingStatusFilter(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="GOOD">GOOD</MenuItem>
                <MenuItem value="CRITICAL">CRITICAL</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={3} xs={12} sm={6}>
            <Autocomplete
              size="small"
              options={serviceList}
              getOptionLabel={(o) => o.name || ''}
              value={selectedService}
              onChange={(e, v) => {
                setSelectedService(v);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              renderInput={(params) => <TextField {...params} label="Service" />}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* CONTENT: MOBILE CARDS VS DESKTOP TABLE */}
      {isMobile ? (
        <Box>
          {loading ? (
            <Box textAlign="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : rows.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, border: '1px solid #e2e8f0', backgroundColor: '#ffffff' }}>
              <Typography color="text.secondary">No coordinator records found</Typography>
            </Paper>
          ) : (
            rows.map((row) => {
              const due = parseFloat(row.balance_to_be_paid_by_today || 0);
              const order = parseFloat(row.order_total_amount || 0);
              const paid = parseFloat(row.paid_total_amount || 0);
              const balance = parseFloat(row.balance_amount || 0);

              return (
                <Paper
                  key={row.id || row.coordinator_id || row.coordinator_name}
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2.5,
                    border: due > 0 ? '1.5px solid #fecaca' : '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1" fontWeight={800} color="#0f172a">
                      {row.coordinator_name || 'Unassigned'}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${row.no_of_companies || 0} clients`}
                      sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f1f5f9', color: '#334155' }}
                    />
                  </Box>

                  {due > 0 && (
                    <Box
                      sx={{
                        mb: 1,
                        p: 1,
                        borderRadius: 2,
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fee2e2',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} color="#dc2626">
                        ⚠️ Due Today
                      </Typography>
                      <Typography variant="body2" fontWeight={800} color="#b91c1c">
                        {formatAmount(due)}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: 2,
                      backgroundColor: '#f8fafc',
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#64748b" display="block" fontSize="0.7rem">
                          Order Total
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#1e293b" noWrap>
                          {formatAmount(order)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#16a34a" display="block" fontSize="0.7rem">
                          Collected
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#15803d" noWrap>
                          {formatAmount(paid)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="#ea580c" display="block" fontSize="0.7rem">
                          Balance
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="#c2410c" noWrap>
                          {formatAmount(balance)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.2} pt={0.8} borderTop="1px solid #f1f5f9">
                    <Typography variant="caption" color="text.secondary">
                      Invoiced: {formatAmount(row.issued_invoice_total_amount)}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<MapOutlinedIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        setSelectedCoordinator(row);
                        setMapModalOpen(true);
                      }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: 2,
                        py: 0.4,
                        px: 1.2,
                        borderColor: '#cbd5e1',
                        color: '#334155',
                      }}
                    >
                      View Map
                    </Button>
                  </Box>
                </Paper>
              );
            })
          )}

          {rowCount > paginationModel.pageSize && (
            <Box display="flex" justifyContent="center" alignItems="center" py={2}>
              <Pagination
                count={Math.ceil(rowCount / paginationModel.pageSize)}
                page={paginationModel.page + 1}
                onChange={(e, p) => setPaginationModel((prev) => ({ ...prev, page: p - 1 }))}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', backgroundColor: '#ffffff', overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            rowHeight={52}
            headerHeight={46}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            rowCount={rowCount}
            disableSelectionOnClick
            sx={{
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f8fafc',
                color: '#475569',
                fontWeight: 700,
                fontSize: '0.82rem',
                borderBottom: '1px solid #e2e8f0',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f1f5f9',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f8fafc',
              },
            }}
          />
        </Paper>
      )}

      <CoordinatorMapModal
        open={mapModalOpen}
        coordinator={selectedCoordinator}
        academicYearId={selectedAcademicYear}
        onClose={() => setMapModalOpen(false)}
      />

      {/* Mobile Bottom Navigation for 1-tap switching between the 3 reports */}
      <ReportsMobileBottomNav />
    </Box>
  );
}

export default CoordinatorWiseReportList;